import { useState } from "react";
import mainBedroom from "@/assets/main-bedroom.png";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { rooms } from "@/mocks/rooms";
import gardenRoom from "@/assets/garden-room.png";

export default function Rooms({ onBook }: { onBook: () => void }) {
  const ref = useScrollReveal();
  const [activeRoom, setActiveRoom] = useState<string | null>(null);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const [bedroomThreeIdx, setBedroomThreeIdx] = useState(0);
  const bedroomThreeImages = [
    "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/cbb9afb134ad8ed3b11109a2585ad810.png",
    "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/db295ba16c5b1b54dbbc90b4919dabdd.png"
  ];
  const [bedroomFourIdx, setBedroomFourIdx] = useState(0);
  const bedroomFourImages = [
    "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/fd37c4dd645a84632e462dfec22beca0.png",
    "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/69afefdd64ec83ec8bf73f28629896e7.png"
  ];

  const activeRoomData = rooms.find((r) => r.slug === activeRoom);
  const activeImages = activeRoomData
    ? [activeRoomData.heroImage, ...activeRoomData.images]
    : [];

  const openRoom = (slug: string) => {
    setActiveRoom(slug);
    setLightboxIdx(null);
    document.body.style.overflow = "hidden";
  };

  const closeRoom = () => {
    setActiveRoom(null);
    setLightboxIdx(null);
    document.body.style.overflow = "";
  };

  const openLightbox = (idx: number) => {
    setLightboxIdx(idx);
  };

  const closeLightbox = () => {
    setLightboxIdx(null);
  };

  return (
    <section id="rooms" ref={ref} className="overflow-hidden" style={{ backgroundColor: "#F0EBE0" }}>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 pt-28 pb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-5" style={{ color: "#4E4228" }}>Accommodation</p>
            <h2 className="fade-in delay-100 font-serif font-medium leading-none" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", color: "#3C3530", textShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
              The Rooms
            </h2>
          </div>
          <p className="fade-in delay-200 font-sans font-light leading-loose text-sm max-w-md md:text-right" style={{ color: "#8C8478" }}>
            The house consists of four bedrooms, a bathroom with a separate toilet, a kitchen, a dining room, a lounge, and a porch. The separate room consists only of a bedroom and a bathroom, and therefore shares the kitchen in the main house.
          </p>
        </div>
      </div>

      {/* Row 1: large landscape + tall portrait */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 mb-3">
        <div className="flex flex-col md:flex-row gap-3 items-stretch">
          <div onClick={() => openRoom("main")} className="img-reveal md:w-[58%] overflow-hidden group relative cursor-pointer" style={{ height: "480px" }}>
            <img alt="Main Bedroom — King-size bed" className="w-full h-full object-cover object-center img-washed group-hover:scale-103 transition-transform duration-1000"
              src={mainBedroom} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,22,8,0.55) 0%, transparent 55%)" }}></div>
            <div className="absolute bottom-0 left-0 p-8">
              <p className="text-sm tracking-[0.1em] uppercase font-sans font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>King-size Bed &middot; En-suite</p>
              <h3 className="font-serif font-light text-white text-2xl">Main Bedroom</h3>
            </div>
          </div>
          <div onClick={() => openRoom("bedroom-two")} className="img-reveal delay-200 md:w-[42%] overflow-hidden group relative cursor-pointer" style={{ height: "480px" }}>
            <img alt="Double Bedroom" className="w-full h-full object-cover object-center img-washed group-hover:scale-103 transition-transform duration-1000"
              src="https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/7331c66536b30aa6da19c32642f7822a.png" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,22,8,0.55) 0%, transparent 55%)" }}></div>
            <div className="absolute bottom-0 left-0 p-8">
              <p className="text-sm tracking-[0.1em] uppercase font-sans font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>Double Bed</p>
              <h3 className="font-serif font-light text-white text-2xl">Bedroom Two</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: two cards */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 mb-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div onClick={() => openRoom("bedroom-three")} className="img-reveal delay-100 md:w-[48%] overflow-hidden group relative cursor-pointer" style={{ height: "320px" }}>
            {bedroomThreeImages.map((src, idx) => (
              <img
                key={idx}
                alt={`Bedroom Three ${idx + 1}`}
                className="w-full h-full object-cover object-center img-washed group-hover:scale-103 transition-all duration-700 absolute inset-0"
                src={src}
                style={{ opacity: bedroomThreeIdx === idx ? 1 : 0 }}
              />
            ))}
            <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2 z-10">
              {bedroomThreeImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setBedroomThreeIdx(idx); }}
                  className="w-2 h-2 rounded-full transition-all cursor-pointer"
                  style={{
                    backgroundColor: bedroomThreeIdx === idx ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)",
                    transform: bedroomThreeIdx === idx ? "scale(1.3)" : "scale(1)"
                  }}
                />
              ))}
            </div>
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,22,8,0.55) 0%, transparent 55%)" }}></div>
            <div className="absolute bottom-0 left-0 p-6">
              <p className="text-sm tracking-[0.1em] uppercase font-sans font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>Twin Beds</p>
              <h3 className="font-serif font-light text-white text-xl">Bedroom Three</h3>
            </div>
          </div>

          <div onClick={() => openRoom("bedroom-four")} className="img-reveal delay-200 md:w-[52%] overflow-hidden group relative cursor-pointer" style={{ height: "320px" }}>
            {bedroomFourImages.map((src, idx) => (
              <img
                key={idx}
                alt={`Bedroom Four ${idx + 1}`}
                className="w-full h-full object-cover object-center img-washed group-hover:scale-103 transition-all duration-700 absolute inset-0"
                src={src}
                style={{ opacity: bedroomFourIdx === idx ? 1 : 0 }}
              />
            ))}
            <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2 z-10">
              {bedroomFourImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setBedroomFourIdx(idx); }}
                  className="w-2 h-2 rounded-full transition-all cursor-pointer"
                  style={{
                    backgroundColor: bedroomFourIdx === idx ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)",
                    transform: bedroomFourIdx === idx ? "scale(1.3)" : "scale(1)"
                  }}
                />
              ))}
            </div>
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,22,8,0.55) 0%, transparent 55%)" }}></div>
            <div className="absolute bottom-0 left-0 p-6">
              <p className="text-sm tracking-[0.1em] uppercase font-sans font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>Twin Beds</p>
              <h3 className="font-serif font-light text-white text-xl">Bedroom Four</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Garden room + bathrooms + CTA */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 mb-3">
        <div className="flex flex-col md:flex-row gap-3 items-stretch">
          <div onClick={() => openRoom("garden-room")} className="img-reveal md:w-[30%] overflow-hidden group relative cursor-pointer">
            <img alt="Garden room" className="w-full h-full object-cover object-center img-washed group-hover:scale-103 transition-transform duration-1000"
              src={gardenRoom} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,22,8,0.55) 0%, transparent 55%)" }}></div>
            <div className="absolute bottom-0 left-0 p-6">
              <p className="text-sm tracking-[0.1em] uppercase font-sans font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>Private &middot; Separate Entrance</p>
              <h3 className="font-serif font-light text-white text-xl">Garden room</h3>
            </div>
          </div>
          <div onClick={() => openRoom("bathrooms")} className="img-reveal delay-100 md:w-[30%] overflow-hidden group relative cursor-pointer">
            <img alt="Bathrooms" className="w-full h-full object-cover object-center img-washed group-hover:scale-103 transition-transform duration-1000"
              src="https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/7942ffcfae1533c98379a0d1f01dcb91.png" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,22,8,0.55) 0%, transparent 55%)" }}></div>
            <div className="absolute bottom-0 left-0 p-6">
              <p className="text-sm tracking-[0.1em] uppercase font-sans font-medium mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>1 bathroom, separate toilet</p>
              <h3 className="font-serif font-light text-white text-xl">Bathrooms</h3>
            </div>
          </div>
          <div className="fade-in md:w-[40%] flex flex-col justify-center px-10 py-12" style={{ backgroundColor: "#F0EBE0", border: "1px solid #D8D0C4" }}>
            <p className="text-base tracking-[0.1em] uppercase font-sans font-medium mb-4" style={{ color: "#4E4228" }}>Self-Catering</p>
            <h3 className="font-serif font-light leading-snug mb-4" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2.2rem)", color: "#3C3530" }}>
              Fully equipped.<br />Completely yours.
            </h3>
            <p className="font-sans font-light leading-loose text-sm mb-3 max-w-sm" style={{ color: "#8C8478" }}>
              A fully equipped kitchen, cozy lounge and dining area, and a fireplace for cooler evenings. Just bring your food, your people, and your sense of adventure. Guests are required to provide for their own firewood.
            </p>
            <ul className="space-y-1.5 mb-8">
              {["Fully equipped kitchen", "Lounge & dining area", "\n\nFireplace for cold evenings"].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm font-sans font-medium" style={{ color: "#7E7265" }}>
                  <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: "#A8B09E" }}></div>
                  {item}
                </li>
              ))}
            </ul>
            <button onClick={onBook}
              className="px-10 py-3.5 text-sm tracking-[0.1em] uppercase font-sans font-medium transition-all whitespace-nowrap cursor-pointer rounded-full w-fit"
              style={{ backgroundColor: "#4E4228", color: "#F5F0E8" }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#302814")}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#4E4228")}
            >
              Book Now
            </button>
          </div>
        </div>
      </div>

      {/* Rates strip */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 pb-28 pt-10">
        <div className="flex flex-col gap-8 py-10 px-10 md:px-14" style={{ backgroundColor: "#F0EBE0", border: "1px solid #D8D0C4" }}>
          <div>
            <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-3" style={{ color: "#4E4228" }}>Rates</p>
          </div>
          <div className="flex flex-col md:flex-row gap-8 md:gap-12">
            <div className="flex-1">
              <h4 className="font-serif text-lg mb-3" style={{ color: "#3C3530" }}>The Main Farmhouse (Sleeps up to 8 Guests)</h4>
              <ul className="space-y-2">
                {["Base Rate: R1 600 per night (includes up to 4 guests)", "Additional Adults: R400 per person, per night", "Children (6–10 years): R200 per child, per night", "Children aged 5 and under: Complimentary stay"].map((term) => (
                  <li key={term} className="flex items-start gap-2 text-sm font-sans font-medium" style={{ color: "#7E7265" }}>
                    <div className="w-1 h-1 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "#A8B09E" }}></div>
                    {term}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1">
              <h4 className="font-serif text-lg mb-3" style={{ color: "#3C3530" }}>The Garden Room (Sleeps 2 Guests)</h4>
              <p className="font-sans font-light text-sm leading-relaxed mb-3" style={{ color: "#8C8478" }}>
                Need a bit of extra space or an additional bathroom? Add our private garden room to accommodate up to 10 guests in total.
              </p>
              <div className="flex items-start gap-2 text-sm font-sans font-medium" style={{ color: "#7E7265" }}>
                <div className="w-1 h-1 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "#A8B09E" }}></div>
                Garden Room Add-on: R800 per night
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-4 border-t" style={{ borderColor: "#D8D0C4" }}>
            <div className="space-y-1">
              <p className="font-sans font-medium text-sm" style={{ color: "#7E7265" }}>Please note:</p>
              <p className="font-sans font-light text-sm" style={{ color: "#8C8478" }}>All rates include 15% VAT.</p>
              <p className="font-sans font-light text-sm" style={{ color: "#8C8478" }}>A minimum stay of 2 nights applies.</p>
              <p className="font-sans font-light text-sm" style={{ color: "#8C8478" }}>Additional housekeeping services for bedrooms, bathrooms, and the kitchen can be arranged on request at R150 per service.</p>
            </div>
            <button onClick={() => document.getElementById('availability')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-10 py-3.5 text-sm tracking-[0.1em] uppercase font-sans font-medium transition-all whitespace-nowrap cursor-pointer rounded-full border"
              style={{ borderColor: "#6E5E40", color: "#6E5E40" }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#4E4228"; e.currentTarget.style.color = "#F5F0E8"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#6E5E40"; }}
            >
              Check Availability
            </button>
          </div>
        </div>
      </div>

      {/* ===== ROOM PHOTO COLLAGE MODAL ===== */}
      {activeRoom && activeRoomData && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" style={{ backgroundColor: "#F0EBE0" }}>
          {/* Close bar */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-14 py-5" style={{ backgroundColor: "rgba(240,235,224,0.92)", backdropFilter: "blur(12px)" }}>
            <h3 className="font-serif font-medium text-xl" style={{ color: "#3C3530" }}>{activeRoomData.name}</h3>
            <button
              onClick={closeRoom}
              className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors"
              style={{ backgroundColor: "rgba(135,145,124,0.15)" }}
            >
              <i className="ri-close-line text-lg" style={{ color: "#87917C" }}></i>
            </button>
          </div>

          {/* Photo collage */}
          <div className="px-4 md:px-8 pb-8 pt-4">
            <div className={`grid gap-3 md:gap-4 ${
              activeImages.length === 1
                ? "grid-cols-1"
                : activeImages.length === 2
                ? "grid-cols-1 md:grid-cols-2"
                : activeImages.length === 3
                ? "grid-cols-1 md:grid-cols-3"
                : "grid-cols-2 md:grid-cols-2"
            }`}>
              {activeImages.map((img, i) => (
                <div
                  key={i}
                  className={`overflow-hidden rounded-lg group cursor-pointer ${
                    activeImages.length === 1
                      ? "col-span-1"
                      : activeImages.length === 3 && i === 0
                      ? "col-span-1 md:col-span-2"
                      : ""
                  }`}
                  style={{ aspectRatio: activeImages.length === 1 ? "16/9" : "4/3" }}
                  onClick={() => openLightbox(i)}
                >
                  <img
                    alt={`${activeRoomData.name} ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    src={img}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== SUB-LIGHTBOX (single image zoom) ===== */}
      {lightboxIdx !== null && activeImages.length > 0 && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          style={{ backgroundColor: "rgba(20,15,8,0.92)" }}
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors"
            style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>

          {lightboxIdx > 0 && (
            <button
              className="absolute left-4 md:left-8 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}
            >
              <i className="ri-arrow-left-line text-white text-lg"></i>
            </button>
          )}

          {lightboxIdx < activeImages.length - 1 && (
            <button
              className="absolute right-4 md:right-8 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}
            >
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </button>
          )}

          <div className="max-w-6xl max-h-[90vh] px-8" onClick={(e) => e.stopPropagation()}>
            <img
              alt={`${activeRoomData?.name} view ${lightboxIdx + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              src={activeImages[lightboxIdx]}
            />
            <p className="text-center text-xs font-sans font-light mt-4" style={{ color: "rgba(255,255,255,0.4)" }}>
              {lightboxIdx + 1} / {activeImages.length}
            </p>
          </div>
        </div>
      )}

    </section>
  );
}